export interface HomeWorkflowCard {
  title: string;
  description: string;
}

export interface HomeImageBreak {
  imageSrc: string;
  eyebrow: string;
  title: string;
  description: string;
}

export interface JourneyBeat {
  title: string;
  description: string;
  imageSrc: string;
  alt: string;
}

export interface JourneyChapter {
  id: string;
  title: string;
  subtitle?: string;
  beats: JourneyBeat[];
}

export interface HomePlatforms {
  eyebrow: string;
  title: string;
  description: string;
  primaryCta: string;
  secondaryCta: string;
  webImageSrc: string;
  webImageAlt: string;
  phoneImageSrc: string;
  phoneImageAlt: string;
}

export const homeCopyEn = {
  hero: {
    eyebrow: 'Portcierge',
    title: 'Your cruise, organized—port by port.',
    subtitle:
      'Embarkation, sea days, and shore days in one calm place—with weather, options lists, and a shareable itinerary—on your phone and on the web.',
    primaryCta: 'Explore what it does',
    secondaryCta: 'See features',
    imageSrc: '/images/cruiseship-view.webp',
    iconSrc: '/images/portcierge-icon.webp',
    iconAlt: 'Portcierge app icon',
  },
  sections: {
    workflows: {
      title: 'Core workflows',
      subtitle: 'The key things Portcierge is designed to do well on a cruise.',
    },
  },
  journey: {
    title: 'Built for cruise travel',
    subtitle:
      'Plan by sailing and port day, then work day-by-day with weather, lodging, and everything you need in one place.',
    chapters: [
      {
        id: 'create',
        title: 'Import or create a cruise',
        beats: [
          {
            title: 'Cruise itineraries with ports',
            description:
              'Add embarkation, sea days, and port calls with dates. Keep your sailing timeline consistent as ports change.',
            imageSrc: '/images/screenshots/app-cruise-overview.webp',
            alt: 'Portcierge cruise list on phone',
          },
          {
            title: 'Build your sailing itinerary',
            description:
              'Set up your cruise plan port by port so the full sailing stays clear as dates and stops shift.',
            imageSrc: '/images/screenshots/app-cruise-plan.webp',
            alt: 'Portcierge cruise planning screen',
          },
          {
            title: 'See ports on the map',
            description: 'Visualize where you’re headed and keep each port call in context with the rest of the sailing.',
            imageSrc: '/images/screenshots/app-port-map.webp',
            alt: 'Portcierge port map view',
          },
          {
            title: 'Share into the app',
            description:
              'Send URLs, text, and files to a shared inbox and optionally extract cruise details before saving into the right port.',
            imageSrc: '/images/screenshots/app-port-details.webp',
            alt: 'Portcierge port details screen',
          },
        ],
      },
      {
        id: 'plan',
        title: 'Plan your days',
        beats: [
          {
            title: 'Day view itinerary',
            description:
              'See each sailing day with weather, scheduled items (meals, excursions, transport, lodging), notes, and tonight’s stay.',
            imageSrc: '/images/screenshots/app-itinerary.webp',
            alt: 'Portcierge day view with weather',
          },
          {
            title: 'Per-port “Options” lists',
            description:
              'Keep a checklist of shore-excursion ideas per port: reorder, edit, mark done, and delete.',
            imageSrc: '/images/screenshots/app-options.webp',
            alt: 'Portcierge shore excursion options list',
          },
          {
            title: 'Optional AI port planning',
            description:
              'Generate highlights and time-blocked plans for port days, then add only the suggestions you want (with conflict checks).',
            imageSrc: '/images/screenshots/app-port-day.webp',
            alt: 'Portcierge port day schedule',
          },
        ],
      },
      {
        id: 'enjoy',
        title: 'Enjoy the cruise—everything organized',
        subtitle: 'Everything you need stays at your fingertips, with trip data local-first on your device.',
        beats: [
          {
            title: 'Country information',
            description:
              'For each port, check visa and entry notes, currency and tipping, ride-hail apps, health basics, embassy contacts, and cruise port details—before you go ashore.',
            imageSrc: '/images/screenshots/app-country-info.webp',
            alt: 'Portcierge country information screen with visa, currency, and port details',
          },
          {
            title: 'Full cruise itinerary',
            description:
              'See the whole sailing in one place—ports, days, and plans—so you always know what’s next ashore or at sea.',
            imageSrc: '/images/screenshots/app-itinerary.webp',
            alt: 'Portcierge full cruise itinerary',
          },
          {
            title: 'Share as PDF',
            description:
              'Export a cruise itinerary PDF when you need to send plans to travel companions, even if they’re not using the app.',
            imageSrc: '/images/screenshots/app-itinerary.webp',
            alt: 'Portcierge full cruise itinerary ready to share',
          },
        ],
      },
    ] satisfies JourneyChapter[],
  },
  platforms: {
    eyebrow: 'Phone and web',
    title: 'Plan at home. Sail with it in your pocket.',
    description:
      'Build your sailing on a big screen, then take the same cruise—ports, days, weather, and options—with you on your phone.',
    primaryCta: 'Plan on the web',
    secondaryCta: 'Get the app',
    webImageSrc: '/images/screenshots/web-day-view.webp',
    webImageAlt: 'Portcierge day view on the web for Santorini with weather and today’s plan',
    phoneImageSrc: '/images/screenshots/app-itinerary.webp',
    phoneImageAlt: 'Portcierge day view on phone',
  } satisfies HomePlatforms,
  portByPortBreak: {
    imageSrc: '/images/greece.webp',
    eyebrow: 'Port-by-port planning',
    title: 'See what’s happening on each day at sea and ashore.',
    description:
      'Weather and itinerary in one view, so you can plan around conditions without bouncing between apps.',
  } satisfies HomeImageBreak,
  imageBreaks: [
    {
      imageSrc: '/images/santorini.webp',
      eyebrow: 'Plan smarter (optional)',
      title: 'Let AI draft your port days.',
      description: 'Generate port highlights and time-blocked plans, then keep only what you want.',
    },
    {
      imageSrc: '/images/cinque-terra.webp',
      eyebrow: 'Capture fast',
      title: 'Share confirmations into your cruise.',
      description: 'Drop links, text, PDFs, and files into a shared inbox, then save the important details into the right port.',
    },
  ] satisfies HomeImageBreak[],
  workflows: [
    {
      title: 'Cruise → ports → days',
      description:
        'Plan multi-port sailings with a clean structure that stays consistent as dates shift and ports change.',
    },
    {
      title: 'AI planning and review',
      description:
        'Generate a port-day plan, then add individual items or bulk-save a day—while avoiding time conflicts.',
    },
    {
      title: 'Shared Items inbox',
      description:
        'Send content from other apps into Portcierge and optionally extract structured details before saving.',
    },
    {
      title: 'Share itinerary as PDF',
      description: 'Export and share a PDF itinerary so everyone has the plan, even if they’re not using the app.',
    },
  ] satisfies HomeWorkflowCard[],
  finalCta: {
    title: 'Ready to plan your next cruise?',
    subtitle: 'Open Portcierge on the web, or get the app for underway.',
    primaryCta: 'Plan on the web',
    secondaryCta: 'Get the app',
  },
} as const;
